import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { trackEvent, AnalyticsEvents } from "@/utils/analytics";
import Footer from "@/components/Footer";
import { games, genres } from "@/data/games";

const VertigoGames = () => {
  const navigate = useNavigate();
  const [selectedGenre, setSelectedGenre] = useState<string>("All Games");


  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const genreParam = params.get('genre');
    if (genreParam && genres.includes(genreParam)) {
      setSelectedGenre(genreParam);
      sessionStorage.setItem('selectedGenre', genreParam);
    } else {
      const savedGenre = sessionStorage.getItem('selectedGenre');
      if (savedGenre && genres.includes(savedGenre)) {
        setSelectedGenre(savedGenre);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenreChange = (genre: string) => {
    setSelectedGenre(genre);
    sessionStorage.setItem('selectedGenre', genre);
    trackEvent(AnalyticsEvents.GENRE_FILTER, { genre });
  };

  const filteredGames = selectedGenre === "All Games"
    ? games
    : games.filter(game => game.genres.includes(selectedGenre));

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-primary leading-tight">
              <span className="block">VR Game</span>
              <span className="block">Experiences</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Beleef intense VR adventures met vrienden, familie of collega's. Van survival horror tot actie-packed shooters - 
              elke game biedt een unieke en onvergetelijke ervaring.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {genres.map((genre) => (
              <Button
                key={genre}
                variant={selectedGenre === genre ? "default" : "outline"}
                onClick={() => handleGenreChange(genre)}
                className="transition-all duration-200"
              >
                {genre}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredGames.map((game) => (
              <Card 
                key={game.slug}
                className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-2 border-border/50 bg-card overflow-hidden cursor-pointer flex flex-col"
                onClick={() => {
                  trackEvent(AnalyticsEvents.GAME_DETAIL_VIEW, { game: game.slug, source: 'experiences_grid' });
                  navigate(`/experiences/${game.slug}`);
                }}
              >
                <div className="relative h-64 overflow-hidden flex-shrink-0 bg-card isolate [contain:paint]">
                  <div
                    role="img"
                    aria-label={game.title}
                    className="absolute inset-0 bg-no-repeat bg-cover transition-[transform,filter] duration-500 ease-out group-hover:scale-105 brightness-95 group-hover:brightness-75"
                    style={{
                      backgroundImage: `url(${game.image})`,
                      backgroundPosition: "center top"
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-background/80">
                    <span className="text-lg font-semibold text-primary">Bekijk Details →</span>
                  </div>
                </div>
                
                <CardHeader className="relative z-10 flex-grow -mt-px bg-card">
                  <CardTitle className="text-2xl">{game.title}</CardTitle>
                  <CardDescription className="text-base">
                    {game.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="relative z-10 space-y-4 mt-auto bg-card">
                  <div className="space-y-2">
                    {game.features.map((feature) => {
                      const Icon = feature.icon;
                      return (
                        <div key={feature.text} className="flex items-center gap-3 text-sm">
                          <Icon className="w-4 h-4 text-primary" />
                          <span className="text-muted-foreground">{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-primary/10 border-primary/20 backdrop-blur-sm">
            <CardHeader className="text-center space-y-4">
              <CardTitle className="text-3xl md:text-4xl">
                Klaar voor jouw VR Adventure?
              </CardTitle>
              <CardDescription className="text-lg">
                Breng jouw team, vrienden of familie samen voor een onvergetelijke VR ervaring. 
                Wij komen naar jouw locatie met alle benodigde equipment.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center pb-8">
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 transition-opacity"
                onClick={() => {
                  trackEvent(AnalyticsEvents.BOOK_NOW, { location: 'experiences_cta' });
                  navigate('/#contact');
                }}
              >
                Neem Contact Op
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VertigoGames;
